import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';
import {
    ContentBox,
} from '@zillow/zrs-comet';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import PhotoUpload from 'app/shared/components/postingPath/PhotoUpload';
import ListingTour from 'app/shared/components/postingPath/ListingTour';
import { PropertiesSelectors } from 'app/shared/selectors';

const propTypes = {
    tours: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        creatorName: PropTypes.string.isRequired,
        imageSrc: PropTypes.string,
        state: PropTypes.oneOf(['active', 'inactive', 'empty']),
    })),
    tourType: PropTypes.oneOf(['amenity', 'singleFamily']),
};

const defaultProps = {
    tours: [],
    tourType: 'amenity',
};

const Media = ({
    tours,
    tourType,
    errorObj,
    formData,
    isListingPhotoProcessing,
    updateListingPhotoProcessingStatus,
    updateFormData,
    updateErrorObj,
    updateStatusMessage,
    statusMessage,
    isInputFormDisabled,
}) => {
    const dispatch = useDispatch();
    const { hasVrModels: show3dTours } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const handleLearnMoreTourClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LEARN_MORE_TOUR_CLICK, {
            label: AnalyticsConstants.TOUR_LOCATION.CARD,
        }));
    };

    const render3dTours = () => (
        <React.Fragment>
            <Text
                marginBottom="xs"
                mediaQuery={{ lg: { marginBottom: 'sm' } }}
                fontType="h4"
                as="div"
            >
                {tourType === 'amenity' ? 'Building Media' : 'Property media'}
            </Text>
            <ListingTour
                formData={formData}
                tours={tours}
                tourType={tourType}
                isDisabled={isInputFormDisabled}
                updateStatusMessage={updateStatusMessage}
                updateFormData={updateFormData}
            />
        </React.Fragment>
    );

    const renderNo3dTourSF = () => (
        <React.Fragment>
            <Text
                marginBottom="xs"
                mediaQuery={{ lg: { marginBottom: 'sm' } }}
                fontType="h4"
                as="div"
            >
                Property media
            </Text>

            <Spacer marginBottom="sm" className="SubSection-Tour">
                <Text
                    marginBottom="xs"
                    mediaQuery={{ lg: { marginBottom: 'sm' } }}
                    fontType="bodyHeading"
                    as="div"
                >
                    3D tour
                </Text>

                <ContentBox highlight="blue">
                    <Text>
                        Let renters tour your home virtually by adding a free 3D tour.&nbsp;
                    </Text>

                    <TextButton
                        as="a"
                        onClick={handleLearnMoreTourClick}
                        href="https://www.zillow.com/marketing/3d-home/"
                        target="_blank"
                    >
                        Learn More
                    </TextButton>
                </ContentBox>
            </Spacer>
        </React.Fragment>
    );

    return (
        <div className="Media">
            {(show3dTours || tourType === 'amenity') ? render3dTours() : renderNo3dTourSF()}
            <Text
                marginBottom="xs"
                fontType="bodyHeading"
                as="div"
                mediaQuery={{ lg: { marginBottom: 'sm' } }}
            >
                {tourType === 'amenity' ? 'Building Photos' : 'Photos'}
            </Text>
            <PhotoUpload
                showHeading={false}
                errorObj={errorObj}
                formData={formData}
                hasError={Boolean(errorObj.photos)}
                isListingPhotoProcessing={isListingPhotoProcessing}
                updateListingPhotoProcessingStatus={updateListingPhotoProcessingStatus}
                updateFormData={updateFormData}
                updateErrorObj={updateErrorObj}
                updateStatusMessage={updateStatusMessage}
                statusMessage={statusMessage}
                isInputFormDisabled={isInputFormDisabled}
            />
        </div>
    );
};

Media.propTypes = propTypes;
Media.defaultProps = defaultProps;

export { Media };
export default Media;
